"use client"

import React, { useEffect, useState } from "react"
import { Globe, Users, UserPlus, HeartHandshake, Lock, Eye } from "lucide-react"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "ui/DropdownMenu"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { PrivacyLevel } from "util/userUtils"
import { ViewerRelation } from "util/constants"
import { Popover, PopoverContent, PopoverTrigger } from "ui/Popover"

/**
 * Privacy option definition
 */
export interface PrivacyOption {
    value: PrivacyLevel;
    label: string;
    icon: React.ElementType;
    description: string;
}

/**
 * Actor privacy component props
 */
interface ActorPrivacyProps {
    /**
     * The current privacy level of the setting
     */
    privacy: PrivacyLevel;
    
    /**
     * The key name for the about setting (e.g., 'bio_birthdate', 'bio_religion')
     */
    settingKey: string;

    /**
     * Callback function when privacy changes
     */
    onPrivacyChange: (privacy: PrivacyLevel, settingKey: string) => void;
    
    /**
     * Whether the component is for a user or team
     */
    actorType: "user" | "team";

    /**
     * Whether the setting is in edit mode (existing setting)
     */
    editMode?: boolean;
    
    /**
     * Optional CSS class name for styling
     */
    className?: string;

    /**
     * Whether to use a mini icon
     */
    miniIcon?: boolean;

    /**
     * Whether to show dropdown menu
     */
    showDropdown?: boolean;

    /**
     * The viewer relation
     */
    viewerRelation?: ViewerRelation;

    /**
     * Whether to disable the privacy component
     */
    disabled?: boolean;
}


/**
 * ActorPrivacy component for managing user/team about setting privacy
 */
const ActorPrivacy: React.FC<ActorPrivacyProps> = ({
    privacy,
    settingKey,
    onPrivacyChange,
    actorType = "user",
    editMode = false,
    className,
    miniIcon = false,
    showDropdown = true,
    viewerRelation = null,
    disabled = false
}) => {
    const [currentPrivacy, setCurrentPrivacy] = useState<PrivacyLevel>(privacy);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [currentViewerRelation, setCurrentViewerRelation] = useState<ViewerRelation>(viewerRelation);

    // Update state when prop changes
    useEffect(() => {
        setCurrentPrivacy(privacy);
        setCurrentViewerRelation(viewerRelation);
    }, [privacy, viewerRelation]);

    /**
     * Privacy options available
     */
    const PRIVACY_OPTIONS: PrivacyOption[] = actorType === "user" ? [
        {
            value: "me",
            label: "Just me",
            icon: Lock,
            description: "Only you can see this information"
        },
        {
            value: "besteas",
            label: "Besteas",
            icon: HeartHandshake,
            description: "Only your besteas can see this"
        },
        {
            value: "follow",
            label: "Followers",
            icon: UserPlus,
            description: "Only people who follow you can see this"
        },
        {
            value: "teams",
            label: "Parteas",
            icon: Users,
            description: "Only mutual partea members can see this"
        },
        {
            value: "public",
            label: "Public",
            icon: Globe,
            description: "Anyone can see this information"
        },
    ] : [
        {
            value: "members",
            label: "Partea Members",
            icon: Lock,
            description: "Only partea members can see this"
        },
        {
            value: "follow",
            label: "Followers",
            icon: UserPlus,
            description: "Only people who follow you can see this"
        },
        {
            value: "teams",
            label: "Parteas",
            icon: Users,
            description: "Only mutual partea members can see this"
        },
        {
            value: "public",
            label: "Public",
            icon: Globe,
            description: "Anyone can see this information"
        }
    ];

    const getViewerMessage = () => {
        if (currentPrivacy === 'me' && currentViewerRelation === 'self') {            
            return `Only you are able to view this.`;
        } else if (currentViewerRelation) {
            switch (currentViewerRelation) {
                case 'admin':
                    return `You are able to view this because you are an admin.`;
                case 'self':
                    return `You are able to view this because you are this user.`;
                case 'creator':
                    return `You are able to view this because you are the creator of this partea`;
                case 'moderator':
                    return `You are able to view this because you are a moderator of this partea`;
                case 'member':
                    return `You are able to view this because you are a member of this partea`;
                case 'bestea':
                    return `You are able to view this because you are a bestea of the user.`;
                case 'follower':
                    if (actorType === 'user') {
                        return `You are able to view this because you are following the user.`;
                    } else {
                        return `You are able to view this because you are following the partea.`;
                    }
                case 'team':
                    if (actorType === 'user') {
                        return `You are able to view this because you are in a partea with the user.`;
                    } else {
                        return `You are able to view this because you are in a partea with the partea.`;
                    }
                default:
                    return `You are able to view this because you are ${currentViewerRelation}.`;
            }
        }
    }

    // Get current privacy option details
    const getCurrentPrivacyOption = () => {
        return PRIVACY_OPTIONS.find(option => option.value === currentPrivacy) || PRIVACY_OPTIONS[0];
    };

    // Handle privacy change
    const handlePrivacyChange = async (newPrivacy: PrivacyLevel) => {
        if (newPrivacy === currentPrivacy) {
            return;
        }
        
        try {
            setIsUpdating(true);
            setCurrentPrivacy(newPrivacy);
            onPrivacyChange(newPrivacy, settingKey);
        } catch (error) {
            console.error(`Error updating ${actorType} privacy for ${settingKey}:`, error);
            toast.error("Error", {
                description: `Failed to update privacy settings`
            });
            // Revert to previous setting
            setCurrentPrivacy(privacy);
        } finally {
            setIsUpdating(false);
        }
    };

    // Current privacy option details
    const currentOption = getCurrentPrivacyOption();
    const PrivacyIcon = currentOption.icon;

    return (
        <div className={`space-y-2 ${!showDropdown && miniIcon && privacy === 'public' ? 'hidden' : ''} ${className}`}>
            <DropdownMenu>
                <div className="flex flex-row items-start">
                    {showDropdown ? (
                        <DropdownMenuTrigger 
                        className={`flex items-center justify-between px-2 py-1.5 text-sm font-medium rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-bg-modal dark:data-[state=open]:bg-eoe-dark-bg-modal ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        disabled={isUpdating || !showDropdown || disabled}
                    >
                        <div className="flex items-center gap-2 text-eoe-text dark:text-eoe-dark-text">
                            <PrivacyIcon className="size-3" />
                            {!miniIcon && <span>{currentOption.label}</span>}
                        </div>
                    </DropdownMenuTrigger>
                    ) : (       
                        <Popover>
                            <PopoverTrigger 
                                className="flex items-center gap-1 text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            onClick={(e) => e.stopPropagation()}
                            >                
                                <div 
                                    className="flex items-center justify-between px-1.5 py-1.5 text-sm font-medium rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-bg-modal dark:data-[state=open]:bg-eoe-dark-bg-modal"
                                    
                                >
                                    <div className="flex items-center gap-2 text-eoe-text dark:text-eoe-dark-text">
                                        <PrivacyIcon className="size-3" />
                                        {!miniIcon && <span>{currentOption.label}</span>}
                                    </div>
                                </div>
                            </PopoverTrigger>
                        <PopoverContent className="max-w-[300px] space-y-1">
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Visible to {currentOption.label}.
                            </p>
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                {getViewerMessage()}
                            </p>
                        </PopoverContent>
                    </Popover>
                    )}
                        <Popover>
                            <PopoverTrigger 
                                className="flex items-center gap-1 text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Eye className="size-3 cursor-pointer" />
                        </PopoverTrigger>
                        <PopoverContent className="max-w-[300px] space-y-1">
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Privacy options are ordered from most private to least private, and each option includes the users in preceding options.
                            </p>
                            <p className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                You can change the default privacy level in <Link to="/settings/privacy" className="underline">settings</Link>.
                            </p>
                        </PopoverContent>
                    </Popover>
                </div>
            
                <DropdownMenuContent className="max-w-[300px]">
                    {PRIVACY_OPTIONS.map((option) => (
                        <DropdownMenuItem 
                            key={option.value}
                            className={`${currentPrivacy === option.value ? "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover" : ""}`}
                            onClick={() => handlePrivacyChange(option.value as PrivacyLevel)}
                        >
                            <option.icon className="size-4 mr-2" />
                            <span>{option.label}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default ActorPrivacy;
